import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import dayjs from '../../utils/dayjsConfig';
import { WeekNavigator } from '../../components';
import { DrawerContentContainer } from '../AdminDrawer/AdminDrawer.styles';
import { StyledAvatar } from '../StyledAvatar/StyledAvatar';
import { TipsEarnedIcon, TipsPaidIcon, DollarIcon } from '../../assets/svg';
import { selectCurrentVenue } from '../../redux/slices/currentUserSlice';
import { getStartOfWeek, calculateWeekDays } from '../../utils/dateUtils';
import { currencyFormatter } from '../../utils/helpers/formatting/currencyHelpers';
import * as tipsHistoryService from './tipsHistoryService';
import { EmployeeSection, EmployeeAvatar, EmployeeName, EmployeeRoles } from './RecordPaymentDrawer.styles';
import {
  BalanceCard,
  BalanceAmount,
  TipsHistoryList,
  TipsHistoryItem,
  TipsHistoryItemSubtitle,
  BalanceText,
  LoadingContainer,
  DayContainer,
  DayHeader,
  DayItemsList,
  IconContainer,
  PayoutContentWrapper,
  BalanceOwedGroup,
} from './TipsHistoryDrawer.styles';
import { Typography } from '@mui/material';

// Exported for testing
export const useTipsHistoryData = (
  employeeId,
  selectedWeek,
  startOfWeekIndex,
  venueTimezone,
  endOfDay,
  services = tipsHistoryService
) => {
  const [weeklyTipsData, setWeeklyTipsData] = useState([]);
  const [employeeNamesByEid, setEmployeeNamesByEid] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeeklyTips = async () => {
      if (!employeeId) return;

      setIsLoading(true);
      setError(null);
      try {
        // Get week days for the selected week
        const weekDays = calculateWeekDays(selectedWeek, startOfWeekIndex);
        const weekDates = weekDays.map((day) => dayjs(day.date));
        const dateStrings = weekDates.map((date) => date.format('YYYY-MM-DD'));

        // Fetch both tips owed and payouts
        const [tipsOwedData, payoutsByDay] = await Promise.all([
          services.getWeeklyTipsOwed(employeeId, dateStrings),
          services.getWeeklyTipPayouts(employeeId, weekDates, venueTimezone, endOfDay),
        ]);

        // Combine dates with tips owed and payouts
        const combinedData = weekDates.map((date, index) => {
          const dateString = date.format('YYYY-MM-DD');
          return {
            date: date,
            tipsOwedData: tipsOwedData[index],
            payouts: payoutsByDay[dateString] || [],
          };
        });

        setWeeklyTipsData(combinedData);

        // Collect unique paidByEid values for payouts that don't have paidByDisplayName
        // (for backward compatibility with older payout documents)
        const eidsNeedingFetch = new Set();
        Object.values(payoutsByDay).forEach((dayPayouts) => {
          dayPayouts.forEach((payout) => {
            if (payout.paidByEid && !payout.paidByDisplayName) {
              eidsNeedingFetch.add(payout.paidByEid);
            }
          });
        });

        // Fetch employee names only for payouts missing paidByDisplayName
        if (eidsNeedingFetch.size > 0) {
          const namesByEid = await services.getEmployeeNamesByEid([...eidsNeedingFetch]);
          setEmployeeNamesByEid(namesByEid);
        }
      } catch (err) {
        console.error('Error fetching weekly tips:', err);
        setError(err);
        setWeeklyTipsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeeklyTips();
  }, [employeeId, selectedWeek, startOfWeekIndex, venueTimezone, endOfDay, services]);

  return { weeklyTipsData, employeeNamesByEid, isLoading, error };
};

const TipsHistoryDrawer = ({ employee, initialWeek = null }) => {
  const venue = useSelector(selectCurrentVenue);
  const startOfWeekIndex = venue?.startOfWeekIndex ?? 0;
  const venueTimezone = venue?.timezone || 'America/Los_Angeles';
  const endOfDay = venue?.endOfDay || 4;

  // Initialize to current week or provided initial week
  const startOfCurrentWeek = initialWeek || getStartOfWeek(Date.now(), startOfWeekIndex);
  const [selectedWeek, setSelectedWeek] = useState(startOfCurrentWeek);

  // Use custom hook for data fetching
  const { weeklyTipsData, employeeNamesByEid, isLoading, error } = useTipsHistoryData(
    employee.id,
    selectedWeek,
    startOfWeekIndex,
    venueTimezone,
    endOfDay
  );

  const handleWeekChange = useCallback(
    (date) => {
      const startOfWeek = getStartOfWeek(date, startOfWeekIndex);
      setSelectedWeek(startOfWeek);
    },
    [startOfWeekIndex]
  );

  // Format the week start date for display
  const formattedWeekStart = dayjs(selectedWeek).format('MMM D');

  return (
    <DrawerContentContainer alignment="stretch">
      {/* Employee Section */}
      <EmployeeSection>
        <EmployeeAvatar>
          <StyledAvatar size="large" alt={employee.displayName || 'User'} src={employee.profileImageUrl} />
        </EmployeeAvatar>
        <EmployeeName variant="h5">{employee.displayName}</EmployeeName>
        <EmployeeRoles variant="body2">
          {employee.shiftTypes && employee.shiftTypes.length > 0 ? employee.shiftTypes.join(', ') : 'Employee'}
        </EmployeeRoles>
      </EmployeeSection>

      <WeekNavigator
        noPadding
        noCalendar
        selectedDate={selectedWeek}
        onWeekChange={handleWeekChange}
        renderTitle={() => `Week of ${formattedWeekStart}`}
      />

      <BalanceCard>
        <BalanceOwedGroup>
          <Typography variant="bodyRegular">Current Balance Owed</Typography>
          <BalanceAmount variant="h4">
            {typeof employee.tipsOwed === 'number' ? currencyFormatter(employee.tipsOwed) : '$0.00'}
          </BalanceAmount>
        </BalanceOwedGroup>
        <DollarIcon size={32} />
      </BalanceCard>

      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={30} />
        </LoadingContainer>
      ) : error ? (
        <Typography variant="body2" color="error">
          Unable to load tips history. Please try again later.
        </Typography>
      ) : (
        <TipsHistoryList>
          {weeklyTipsData.map(({ date, tipsOwedData, payouts }) => {
            const hasTipsOwed = tipsOwedData && tipsOwedData.tipsOwed !== 0;
            const hasPayouts = payouts && payouts.length > 0;
            const hasAnyData = hasTipsOwed || hasPayouts;

            // Skip days with no data
            if (!hasAnyData) return null;

            return (
              <DayContainer key={date.format('YYYY-MM-DD')}>
                {/* Day Header */}
                <DayHeader variant="subtitle2">{date.format('ddd D').toUpperCase()}</DayHeader>

                <DayItemsList>
                  {/* Tips Owed Entry */}
                  {hasTipsOwed && (
                    <TipsHistoryItem hasData={true} isLast={!hasPayouts}>
                      <IconContainer>
                        <TipsEarnedIcon size={40} />
                      </IconContainer>
                      <Typography variant="bodyRegular">
                        {tipsOwedData.tipsOwed > 0 ? '+ ' : ''}
                        {currencyFormatter(tipsOwedData.tipsOwed)} Tips Owed
                      </Typography>
                      <BalanceText variant="caption">
                        Balance: {currencyFormatter(tipsOwedData.balanceAfter)}
                      </BalanceText>
                    </TipsHistoryItem>
                  )}

                  {/* Payout Entries */}
                  {hasPayouts &&
                    payouts.map((payout, index) => {
                      const payoutTime = dayjs(payout.timestamp.seconds * 1000).tz(venueTimezone);
                      // Prefer paidByDisplayName from the document, fall back to fetched name for older documents
                      const payerName = payout.paidByDisplayName || employeeNamesByEid[payout.paidByEid] || 'Unknown';
                      const displayName = payerName.length > 20 ? `${payerName.substring(0, 20)}...` : payerName;
                      const paymentAmount = currencyFormatter(payout.amount);
                      const isLastItem = index === payouts.length - 1;

                      return (
                        <TipsHistoryItem key={payout.id} hasData={true} isLast={isLastItem}>
                          <IconContainer>
                            <TipsPaidIcon size={40} />
                          </IconContainer>
                          <PayoutContentWrapper>
                            <Typography variant="bodyRegular">
                              - {paymentAmount} paid by {displayName}
                            </Typography>
                            <TipsHistoryItemSubtitle>{payoutTime.format('h:mm A')}</TipsHistoryItemSubtitle>
                          </PayoutContentWrapper>
                          <BalanceText variant="caption">Balance: {currencyFormatter(payout.balanceAfter)}</BalanceText>
                        </TipsHistoryItem>
                      );
                    })}
                </DayItemsList>
              </DayContainer>
            );
          })}
        </TipsHistoryList>
      )}
    </DrawerContentContainer>
  );
};

TipsHistoryDrawer.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    profileImageUrl: PropTypes.string,
    shiftTypes: PropTypes.arrayOf(PropTypes.string),
    tipsOwed: PropTypes.number,
  }).isRequired,
  handleCloseDrawer: PropTypes.func,
  initialWeek: PropTypes.number, // Unix timestamp for initial week
};

export default TipsHistoryDrawer;
